import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/use-toast";
import {
	PrintDensity,
	PrintDensityName,
	Size,
	Spacing,
	Text,
	Barcode as ZPLBarcode,
	BarcodeType as ZPLBarcodeType,
	Label as ZPLLabel,
} from "jszpl";
import qz from "qz-tray";
import { useEffect, useState } from "react";
import Barcode from "react-barcode";


// Enum for ZPL output method
enum ZPLOutputMethod {
	DOWNLOAD = "Download",
	QZ_TRAY = "QZ-Tray",
}

interface BarcodeModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initialData?: {
		barcodeNumber?: string;
		pharmacyName?: string;
		productName?: string;
		expiryDate?: string;
	};
	onPrintComplete?: (barcodeNumber: string) => void;
}

export default function BarcodeModal({
	open,
	onOpenChange,
	initialData = {},
	onPrintComplete,
}: BarcodeModalProps) {
	// Component states
	const [barcodeNumber, setBarcodeNumber] = useState<string>(initialData.barcodeNumber || "");
	const [pharmacyName, setPharmacyName] = useState<string>(initialData.pharmacyName || "");
	const [productName, setProductName] = useState<string>(initialData.productName || "");
	const [expiryDate, setExpiryDate] = useState<string>(initialData.expiryDate || "");
	const [zplOutputMethod, setZplOutputMethod] = useState<ZPLOutputMethod>(ZPLOutputMethod.DOWNLOAD);
	const [qzConnected, setQzConnected] = useState<boolean>(false);
	const [availablePrinters, setAvailablePrinters] = useState<string[]>([]);
	const [selectedPrinter, setSelectedPrinter] = useState<string>("");

	// Update states when initialData changes
	useEffect(() => {
		if (initialData.barcodeNumber) setBarcodeNumber(initialData.barcodeNumber);
		if (initialData.pharmacyName) setPharmacyName(initialData.pharmacyName);
		if (initialData.productName) setProductName(initialData.productName);
		if (initialData.expiryDate) setExpiryDate(initialData.expiryDate);
	}, [initialData]);

	// Initialize QZ-Tray connection
	useEffect(() => {
		const initializeQZ = async () => {
			try {
				if (typeof qz.security !== 'undefined') {
					qz.security.setCertificatePromise(Promise.resolve(""));
				}
				// Configure WebSocket to prefer secure connection
				if (!qz.websocket.isActive()) {
					await qz.websocket.connect();
					setQzConnected(true);

					console.log("QZ-Tray connected successfully using WebSocket protocol (wss:// or ws://)");

					// Get available printers
					const printers = await qz.printers.find();
					setAvailablePrinters(printers);
					console.log("Available printers:", printers);

					// Set default printer
					const defaultPrinter = await qz.printers.getDefault();
					setSelectedPrinter(defaultPrinter);
					console.log("Default printer selected:", defaultPrinter);
				}
			} catch (error) {
				console.error("Failed to connect to QZ-Tray:", error);
				setQzConnected(false);
				
				// Provide user feedback about the connection issue
				toast({
					title: "QZ-Tray Connection Failed",
					variant: "destructive",
					description: "Could not connect to QZ-Tray. Please ensure QZ-Tray is running and try again.",
					duration: 5000,
				});
			}
		};

		if (open) {
			initializeQZ();
		}

		// Cleanup on unmount
		return () => {
			if (qz.websocket.isActive()) {
				qz.websocket.disconnect().catch(console.error);
			}
		};
	}, [open]);

	// Generate ZPL command for barcode sticker
	const generateZPLSticker = (
		pharmacyName: string,
		productName: string,
		expiryDate: string,
		code: string,
	): string => {
		

		try {
			const labelSize = new Size(406, 203);
			const labelSpacing = new Spacing(0, 0);
			const printDensity = new PrintDensity(PrintDensityName["8dpmm"]);

			const label = new ZPLLabel(labelSize, labelSpacing, printDensity);

			// Pharmacy Name at the top
			const pharmacyText = new Text(10, 20, "A", 16, pharmacyName);
			label.add(pharmacyText);

			// Barcode in the middle
			const barcodeType = new ZPLBarcodeType('CODE128');
			const barcode = new ZPLBarcode(50, 60, barcodeType, code, 50);
			label.add(barcode);

			// Product name and expiry date at the bottom
			const productExpiryText = new Text(10, 170, "A", 12, `${productName} Exp Date: ${expiryDate}`);
			label.add(productExpiryText);

			return label.generateZPL();
		} catch (error) {
			console.error("Error generating ZPL with jszpl:", error);
			return `
				^XA
				^MMT
				^PW406
				^LL203
				^LS0
				^FT10,30^A0N,16,14^FD${pharmacyName}^FS
				^FT50,110^BCN,50,Y,N,N^FD${code}^FS
				^FT10,180^A0N,12,10^FD${productName} Exp. Date: ${expiryDate}^FS
				^XZ`.trim();
		}
	};

	// Print sticker using ZPL
	const printZPLSticker = async () => {
		const zplCommands = generateZPLSticker(
			pharmacyName,
			productName,
			expiryDate,
			barcodeNumber,
		);

		if (zplOutputMethod === ZPLOutputMethod.QZ_TRAY) {
			try {
				if (!qzConnected) {
					toast({
						title: "Error",
						variant: "destructive",
						description: "QZ-Tray is not connected. Please ensure QZ-Tray is running.",
						duration: 5000,
					});
					return;
				}

				if (!selectedPrinter) {
					toast({
						title: "Error",
						variant: "destructive",
						description: "No printer selected. Please select a printer.",
						duration: 5000,
					});
					return;
				}

				const config = qz.configs.create(selectedPrinter);
				await qz.print(config, [zplCommands]);
				
				toast({
					title: "Success",
					variant: "default",
					description: "Label sent to printer successfully!",
					duration: 5000,
				});
			} catch (error) {
				console.error("Failed to print via QZ-Tray:", error);
				toast({
					title: "Error",
					variant: "destructive",
					description: `Failed to print: ${error instanceof Error ? error.message : "Unknown error"}`,
					duration: 5000,
				});
			}
		} else {
			const blob = new Blob([zplCommands], { type: "text/plain" });
			const url = URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.download = `barcode-sticker-${Date.now()}.zpl`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			URL.revokeObjectURL(url);

			toast({
				title: "Success",
				variant: "default", 
				description: "ZPL file downloaded successfully!",
				duration: 5000,
			});
		}
	};

	// Print sticker using HTML

	// Handle print button click
	const handlePrintSticker = () => {
		// Always use ZPL format
		printZPLSticker();

		// Call completion callback and close modal
		if (onPrintComplete) {
			onPrintComplete(barcodeNumber);
		}
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Generate & Print Barcode Sticker</DialogTitle>
					<DialogDescription>
						Configure and print a barcode sticker for your inventory item.
					</DialogDescription>
				</DialogHeader>

				<div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
					{/* Configuration Panel */}
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Barcode Configuration</CardTitle>
								<CardDescription>Select print settings and barcode type</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{/* ZPL Output Method */}
								<div className="space-y-3">
									<Label>ZPL Output Method:</Label>
									<RadioGroup
										value={zplOutputMethod}
										onValueChange={(value) =>
											setZplOutputMethod(value as ZPLOutputMethod)
										}
										className="flex gap-6"
									>
										{Object.values(ZPLOutputMethod).map((method) => (
											<div key={method} className="flex items-center space-x-2">
												<RadioGroupItem value={method} id={method} />
												<Label htmlFor={method} className="cursor-pointer text-sm">
													{method}
												</Label>
											</div>
										))}
									</RadioGroup>

									{/* QZ-Tray Status */}
									{zplOutputMethod === ZPLOutputMethod.QZ_TRAY && (
										<div className="space-y-3 p-3 border rounded-lg bg-muted/30">
											<div className="flex items-center justify-between">
												<span className="text-sm font-medium">QZ-Tray Status:</span>
												<span
													className={`text-sm ${qzConnected ? "text-green-600" : "text-red-600"}`}
												>
													{qzConnected ? "Connected" : "Disconnected"}
												</span>
											</div>

											{qzConnected && availablePrinters.length > 0 && (
												<div className="space-y-2">
													<Label htmlFor="printer-select">Select Printer:</Label>
													<select
														id="printer-select"
														value={selectedPrinter}
														onChange={(e) => setSelectedPrinter(e.target.value)}
														className="w-full p-2 border border-gray-300 rounded-md text-sm"
													>
														{availablePrinters.map((printer) => (
															<option key={printer} value={printer}>
																{printer}
															</option>
														))}
													</select>
												</div>
											)}
										</div>
									)}
								</div>
							</CardContent>
						</Card>
					</div>

					{/* Preview Panel */}
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Preview</CardTitle>
								<CardDescription>Preview of the barcode sticker</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Text Information */}
								<div className="space-y-2 text-sm">
									<div>
										<strong>Pharmacy Name:</strong> {pharmacyName || "No title"}
									</div>
									<div>
										<strong>Expiry Date:</strong> {expiryDate}
									</div>
									<div>
										<strong>Barcode:</strong> {barcodeNumber || "No barcode"}
									</div>
								</div>

								{/* Visual Preview */}
								{pharmacyName && barcodeNumber && (
									<div className="mt-4">
										<h4 className="text-sm font-medium mb-2">Sticker Preview:</h4>
										<div
											className="bg-white border border-gray-300 p-2 mx-auto shadow-sm"
											style={{
												width: "192px",
												height: "96px",
												display: "flex",
												flexDirection: "column",
												justifyContent: "space-between",
												alignItems: "center",
												fontSize: "8px",
												fontFamily: "Arial, sans-serif",
											}}
										>
											{/* Title */}
											<div
												style={{
													fontSize: "8px",
													fontWeight: "bold",
													textAlign: "center",
													width: "100%",
													lineHeight: "1.1",
												}}
											>
												{pharmacyName}
											</div>

											{/* Barcode */}
											<div
												style={{
													flex: 1,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: "100%",
													flexDirection: "column",
												}}
											>
												<Barcode
													value={barcodeNumber}
													format="CODE128"
													width={1}
													height={30}
													fontSize={6}
													margin={0}
													background="white"
												/>
											</div>

											{/* Date */}
											<div
												style={{
													fontSize: "6px",
													textAlign: "center",
													width: "100%",
												}}
											>
												{productName} Exp Date:{new Date(expiryDate).toLocaleDateString()}
											</div>
										</div>
									</div>
								)}
								<CardFooter>
									<Button
									className="w-full"
									disabled={!pharmacyName || !productName || !barcodeNumber || !expiryDate}
									onClick={handlePrintSticker}
								>
									Confirm & Export
								</Button>
								</CardFooter>
							</CardContent>
						</Card>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}